import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { TypographyVariant } from "../../../constants/typography";
import { Typography } from "../../core/Typography/Typography";
import { Image } from "../../core/Image/Image";
import { useStyles } from "./ConsoleRecentPlayCard.style";
import { Game, GameSession, gameQueueStatusGQL } from "@onmo/graphql/graphql";
import { NavigationMd } from "../../../models/navigation";
import { PageRoutes } from "../../../constants/routes";
import { getRoutePath } from "../../../models/location";
import { Skeleton } from "../../core/Skeleton";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { useNavigationBar } from "../../../hooks/useNavigationBar";
import { NavItem } from "../../../types/navigationBar";
import { Icon } from "../../core/Icon/Icon";
import { checkBYOGGame, getGameTitle } from "../../../models/game";
import { useTranslation } from "react-i18next";

const getGameType = (category?: string): "essentials" | "byog" => {
  if (!category) return "essentials";
  try {
    const parsed: string[] = JSON.parse(category);
    return parsed.includes("BYOG") ? "byog" : "essentials";
  } catch {
    return "essentials";
  }
};

const getGameInfo = (game: Game | GameSession) =>
  game.__typename === "GameSession"
    ? {
        id: (game as GameSession).game.id,
        title: (game as GameSession).game.title,
        category: (game as GameSession).game.category,
      }
    : {
        id: (game as Game).id,
        title: (game as Game).title,
        category: (game as Game).category,
      };

export const ConsoleRecentPlayCard: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recentlyPlayedGames = useNavigationBar(true);
  const { data: dataQueue } = useQuery(gameQueueStatusGQL);

  if (!recentlyPlayedGames.loading && recentlyPlayedGames.length === 0)
    return null;

  const onPlayGame = (game: Game | GameSession | undefined) => {
    if (!game) return;

    const { id, title, category } = getGameInfo(game);

    AnalyticsGA4.gameSelect("console", getGameType(category), title);

    navigate(`${PageRoutes.Game}/${id}`, {
      state: {
        prevPath: getRoutePath(),
        isFromGameTab: true,
      },
    });
  };

  return (
    <div className={classes.root}>
      <Typography
        variant={TypographyVariant.HeadingH5Bold}
        className={classes.title}
      >
        {t("Console_Continue Playing")}
      </Typography>
      <div className={classes.gameList}>
        {recentlyPlayedGames.loading ? (
          <div className={classes.gameLoading}>
            {Array.from(new Array(5)).map((_, key) => (
              <div key={key} className={classes.rootGame}>
                <Skeleton className={classes.loadingImage} variant="rect" />
              </div>
            ))}
          </div>
        ) : (
          recentlyPlayedGames.map((item: NavItem, idx) => {
            const isGameLastActive = item.game?.__typename === "GameSession";
            const { src1x, src2x, src3x } = NavigationMd.getContent(item.game);
            const game = isGameLastActive
              ? (item.game as GameSession | undefined)?.game
              : (item.game as Game | undefined);
            const isBYOG = checkBYOGGame(game);

            return (
              <div
                key={idx}
                className={classes.rootGame}
                data-gamepad={`game-card-recent`}
                data-gamepad-id={`game-card-recent-${item.game?.id}`}
                data-selected-class={classes.selected}
                role="button"
                tabIndex={-1}
                aria-label={getGameTitle(game) || undefined}
                onClick={() => onPlayGame(item?.game)}
              >
                <Image
                  src={src1x}
                  src2x={src2x}
                  src3x={src3x}
                  className={classes.image}
                />
                {isGameLastActive && (
                  <div className={classes.gameLastActiveDot} />
                )}
                {dataQueue?.gameQueueStatus?.game?.id === item?.game?.id && (
                  <div className={classes.hourGlassBadge}>
                    <Icon name="icon-hour-glass" size={16} />
                  </div>
                )}
                {isBYOG && (
                  <div className={classes.steam}>
                    <div className={classes.info}>
                      <Icon
                        name="icon-tag"
                        size={20}
                        className={classes.value}
                      />
                      <Typography
                        variant={TypographyVariant.BodyB2Regular}
                        className={classes.value}
                      >
                        Steam
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
